import React, { useEffect, useRef, useCallback } from "react";
import classnames from "classnames";
import PropTypes from "prop-types";
import FacilityCard from "components/organisms/search/FacilityCards/FacilityCard";
import SearchToolbar from "components/organisms/search/SearchToolbar/SearchToolbar";
import { TrackingClient } from "@sparefoot/tracking-client";
import { useLocation } from "react-router";

import "./FacilityCards.scss";

const FacilityCards = ({
	cards = [],
	layout,
	totalListings,
	selectedFacilityId,
	hoveredFacilityId,
	onFacilityHover,
	subPageType
}) => {
	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	const searchTerm = searchParams.get("location");
	const hoverTimeoutRef = useRef(null);

	// Clear timeout on unmount
	useEffect(
		() => () => {
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
			}
		},
		[]
	);

	const handleMouseEnter = useCallback(
		(facilityId) => {
			// Clear any pending clear timeout
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
				hoverTimeoutRef.current = null;
			}
			onFacilityHover?.(facilityId);
		},
		[onFacilityHover]
	);

	const handleMouseLeave = useCallback(() => {
		// Delay clearing hover to prevent flicker when moving between cards
		hoverTimeoutRef.current = setTimeout(() => {
			onFacilityHover?.(null);
			hoverTimeoutRef.current = null;
		}, 50);
	}, [onFacilityHover]);

	useEffect(() => {
		if (cards.length > 0) {
			const facilitiesList = cards.map((facility) => facility.id);
			const facilities = cards.map((facility, index) => ({
				id: facility.id,
				name: facility.name,
				address: facility.address,
				facility_rank: index + 1
			}));
			TrackingClient.trackFacilityListViewed({
				facilities,
				facilities_list: facilitiesList,
				search_term: searchTerm
			});
		}
	}, [cards, searchTerm]);

	return (
		<>
			{cards.map((facility, index) => (
				<React.Fragment key={facility.id}>
					<div
						id={`facility-card-${facility.id}`}
						className={classnames("facility-card-wrapper", {
							// Highlight if selected (clicked) OR hovered - both can be active at the same time
							"facility-card-wrapper--selected":
								selectedFacilityId === facility.id ||
								hoveredFacilityId === facility.id
						})}
						onMouseEnter={() => handleMouseEnter(facility.id)}
						onMouseLeave={handleMouseLeave}
					>
						<FacilityCard
							layout={layout}
							{...facility}
							hasOnlineMoveins={facility.hasOnlineMoveins}
							subPageType={subPageType}
							cardIndex={index}
						/>
					</div>
					{index + 1 < cards.length && (index + 1) % 20 === 0 && (
						<div className="search-toolbar-row">
							<SearchToolbar
								className="search-toolbar divider"
								count={totalListings}
								currentRange={`${index + 2}-${
									index + 21 > totalListings
										? totalListings
										: index + 21
								}`}
								showSortSelect={false}
							/>
						</div>
					)}
				</React.Fragment>
			))}
		</>
	);
};

FacilityCards.propTypes = {
	cards: PropTypes.arrayOf(PropTypes.object),
	layout: PropTypes.oneOf(["wide", "narrow"]),
	totalListings: PropTypes.number,
	selectedFacilityId: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number
	]),
	hoveredFacilityId: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number
	]),
	onFacilityHover: PropTypes.func,
	subPageType: PropTypes.string
};

export default FacilityCards;
